import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { dashboardInputClassName } from "./formStyles";

export type MonthDayCalendarValue = {
  month: string;
  day: string;
};

export const calendarMonthOptions = [
  { label: "January", shortLabel: "Jan", days: 31 },
  { label: "February", shortLabel: "Feb", days: 29 },
  { label: "March", shortLabel: "Mar", days: 31 },
  { label: "April", shortLabel: "Apr", days: 30 },
  { label: "May", shortLabel: "May", days: 31 },
  { label: "June", shortLabel: "Jun", days: 30 },
  { label: "July", shortLabel: "Jul", days: 31 },
  { label: "August", shortLabel: "Aug", days: 31 },
  { label: "September", shortLabel: "Sep", days: 30 },
  { label: "October", shortLabel: "Oct", days: 31 },
  { label: "November", shortLabel: "Nov", days: 30 },
  { label: "December", shortLabel: "Dec", days: 31 },
];

type CalendarDateInputBaseProps = {
  ariaLabel: string;
  className?: string;
  // Shown when a fullDate value is empty (e.g. unset filters) instead of
  // falling back to today's date.
  placeholder?: string;
  disabled?: boolean;
  inputClassName?: string;
  // Lets form wrappers associate a <label htmlFor> with the field trigger.
  id?: string;
  // Side of the field the calendar icon sits on. Defaults to the original
  // right-side placement used across Chamber Settings; Members forms render
  // it on the left per the member-module designs (MEM-CREATE-008).
  iconPosition?: "left" | "right";
};

type CalendarDateInputProps =
  | (CalendarDateInputBaseProps & {
      mode: "monthDay";
      value: MonthDayCalendarValue;
      onChange: (value: MonthDayCalendarValue) => void;
    })
  | (CalendarDateInputBaseProps & {
      mode: "fullDate";
      value: string;
      onChange: (value: string) => void;
    });

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getMonthIndex = (month: string) => {
  const monthIndex = calendarMonthOptions.findIndex(
    ({ label }) => label === month
  );

  return monthIndex >= 0 ? monthIndex : 0;
};

const clampDayForMonth = (monthIndex: number, day: string) => {
  const parsedDay = Number(day) || 1;
  const maxDay = calendarMonthOptions[monthIndex].days;

  return String(Math.min(Math.max(parsedDay, 1), maxDay));
};

const parseDateInputValue = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return new Date();
  }

  const date = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return new Date();
  }

  return date;
};

const toDateInputValue = ({
  year,
  monthIndex,
  day,
}: {
  year: number;
  monthIndex: number;
  day: number;
}) => {
  const paddedMonth = String(monthIndex + 1).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");

  return `${year}-${paddedMonth}-${paddedDay}`;
};

const formatFullDateValue = (value: string) => {
  const date = parseDateInputValue(value);

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const calendarButtonClassName =
  "h-9 rounded-md text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40";

const CalendarDateInput = (props: CalendarDateInputProps) => {
  const {
    ariaLabel,
    className = "",
    disabled = false,
    inputClassName = `${dashboardInputClassName} w-full`,
    id,
    iconPosition = "right",
    mode,
  } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [visibleMonthIndex, setVisibleMonthIndex] = useState(() =>
    mode === "monthDay"
      ? getMonthIndex(props.value.month)
      : new Date().getMonth()
  );
  const [visibleYear, setVisibleYear] = useState(() =>
    new Date().getFullYear()
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    document.addEventListener("keydown", handleDocumentKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) {
      return;
    }

    if (mode === "monthDay") {
      setVisibleMonthIndex(getMonthIndex(props.value.month));
    } else {
      const date = parseDateInputValue(props.value);
      setVisibleMonthIndex(date.getMonth());
      setVisibleYear(date.getFullYear());
    }

    setOpen(true);
  };

  const isEmptyFullDate = mode === "fullDate" && !props.value;
  const displayValue =
    mode === "monthDay"
      ? `${props.value.month || calendarMonthOptions[0].label} ${
          props.value.day || "1"
        }`
      : isEmptyFullDate
      ? props.placeholder || "Select Date"
      : formatFullDateValue(props.value);

  const handleMonthSelect = (monthIndex: number) => {
    if (mode !== "monthDay") {
      return;
    }

    setVisibleMonthIndex(monthIndex);
    props.onChange({
      month: calendarMonthOptions[monthIndex].label,
      day: clampDayForMonth(monthIndex, props.value.day),
    });
  };

  const handleMonthDaySelect = (day: number) => {
    if (mode !== "monthDay") {
      return;
    }

    props.onChange({
      month: calendarMonthOptions[visibleMonthIndex].label,
      day: String(day),
    });
    setOpen(false);
  };

  const moveVisibleMonth = (offset: number) => {
    const nextDate = new Date(visibleYear, visibleMonthIndex + offset, 1);
    setVisibleMonthIndex(nextDate.getMonth());
    setVisibleYear(nextDate.getFullYear());
  };

  const handleFullDateSelect = (day: number) => {
    if (mode !== "fullDate") {
      return;
    }

    props.onChange(
      toDateInputValue({
        year: visibleYear,
        monthIndex: visibleMonthIndex,
        day,
      })
    );
    setOpen(false);
  };

  const renderMonthDayCalendar = () => {
    const selectedMonthIndex =
      mode === "monthDay" ? getMonthIndex(props.value.month) : 0;
    const selectedDay = mode === "monthDay" ? Number(props.value.day) : 1;
    const days = calendarMonthOptions[visibleMonthIndex].days;

    return (
      <>
        <div className="grid grid-cols-3 gap-2">
          {calendarMonthOptions.map((month, monthIndex) => {
            const selected = selectedMonthIndex === monthIndex;

            return (
              <button
                key={month.label}
                type="button"
                onClick={() => handleMonthSelect(monthIndex)}
                className={`${calendarButtonClassName} ${
                  selected
                    ? "bg-cpaGold text-white"
                    : "bg-gray-100 text-gray-950 hover:bg-amber-50"
                }`}
              >
                {month.shortLabel}
              </button>
            );
          })}
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1">
          {Array.from({ length: days }, (_, index) => {
            const day = index + 1;
            const selected =
              selectedMonthIndex === visibleMonthIndex && selectedDay === day;

            return (
              <button
                key={day}
                type="button"
                onClick={() => handleMonthDaySelect(day)}
                className={`${calendarButtonClassName} ${
                  selected
                    ? "bg-cpaGold text-white"
                    : "bg-white text-gray-950 hover:bg-amber-50"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderFullDateCalendar = () => {
    const selectedValue = mode === "fullDate" ? props.value : "";
    const daysInMonth = new Date(
      visibleYear,
      visibleMonthIndex + 1,
      0
    ).getDate();
    const firstWeekday = new Date(visibleYear, visibleMonthIndex, 1).getDay();
    const blankCells = Array.from(
      { length: firstWeekday },
      (_, index) => index
    );

    return (
      <>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => moveVisibleMonth(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-700 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
            aria-label="Previous month"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="text-sm font-bold text-gray-950">
            {calendarMonthOptions[visibleMonthIndex].label} {visibleYear}
          </div>
          <button
            type="button"
            onClick={() => moveVisibleMonth(1)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-700 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold/40"
            aria-label="Next month"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1 text-center text-xs font-bold uppercase text-gray-500">
          {weekdayLabels.map((weekday) => (
            <div key={weekday}>{weekday}</div>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-7 gap-1">
          {blankCells.map((index) => (
            <div key={`blank-${index}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, index) => {
            const day = index + 1;
            const value = toDateInputValue({
              year: visibleYear,
              monthIndex: visibleMonthIndex,
              day,
            });
            const selected = selectedValue === value;

            return (
              <button
                key={day}
                type="button"
                onClick={() => handleFullDateSelect(day)}
                className={`${calendarButtonClassName} ${
                  selected
                    ? "bg-cpaGold text-white"
                    : "bg-white text-gray-950 hover:bg-amber-50"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            handleOpen();
          }
        }}
        className={`${inputClassName} relative text-left ${
          iconPosition === "left" ? "pl-12" : "pr-12"
        }`}
      >
        <span className={`block truncate ${isEmptyFullDate ? "text-gray-400" : ""}`}>{displayValue}</span>
        <FontAwesomeIcon
          icon={faCalendar}
          className={`pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 ${
            iconPosition === "left" ? "left-4" : "right-4"
          }`}
        />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={`${ariaLabel} calendar`}
          className="absolute left-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
        >
          {mode === "monthDay"
            ? renderMonthDayCalendar()
            : renderFullDateCalendar()}
        </div>
      )}
    </div>
  );
};

export default CalendarDateInput;
